import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSongCart } from "../../../context/SongCartContext";
import { useActiveRequests } from "../../hooks/useActiveRequests";
import SongCard from "../common/SongCard";
import ExternalSongCard from "../common/ExternalSongCard";
import DedicationMaster from './DedicationModal/DedicationMaster';
import SuccessModal from "./SuccessModal";
import ThankDialog from "./ThankDialog";
import { useSnackbar } from "notistack";
import { getDeviceId } from "../../../utils/cookieUtils";

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:3001";

const SearchModal = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [localResults, setLocalResults] = useState([]);
  const [externalResults, setExternalResults] = useState([]);
  const [suggestedSongs, setSuggestedSongs] = useState([]);
  const [externalError, setExternalError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [requestedExternal, setRequestedExternal] = useState({});
  const [selectedSong, setSelectedSong] = useState(null);
  const [showDedicationModal, setShowDedicationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const [requesterName, setRequesterName] = useState("");
  const { cart, clearCart } = useSongCart();
  const { enqueueSnackbar } = useSnackbar();
  const debounceRef = useRef(null);

  const {
    data: activeRequests = {},
    isLoading: requestsLoading,
    refetch: refetchActiveRequests
  } = useActiveRequests({
    enabled: open,
  });

  const fetchSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/songs/popular`);
      if (res.ok) {
        const data = await res.json();
        setSuggestedSongs(
          (Array.isArray(data) ? data : []).slice(0, 12).map((song) => ({
            ...song,
            id: song.songs_master_id,
          }))
        );
      }
    } catch (err) {
      console.error("Suggestions load error:", err);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const runSmartSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setLocalResults([]);
      setExternalResults([]);
      setExternalError(null);
      return;
    }

    setSearchLoading(true);
    setExternalError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/songs/smart-search?q=${encodeURIComponent(trimmed)}&limit=20`
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || data.error || `Search failed (${res.status})`);
      }
      setLocalResults(data.local || []);
      setExternalResults(data.external || []);
      setExternalError(data.externalError || null);
    } catch (err) {
      console.error("Smart search error:", err);
      enqueueSnackbar(
        err.message || "Search failed. Is the server running on port 3001?",
        { variant: "error" }
      );
      setLocalResults([]);
      setExternalResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      fetchSuggestions();
    } else {
      setSearchQuery("");
      setLocalResults([]);
      setExternalResults([]);
      setExternalError(null);
      setRequestedExternal({});
      setSuggestedSongs([]);
    }
  }, [open, fetchSuggestions]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSmartSearch(val), 400);
  };

  const handleRequestSuccess = () => {
    refetchActiveRequests();
  };

  const handleRequestOpen = (song) => {
    setSelectedSong(song);
    setShowDedicationModal(true);
  };

  const handleRequestToAdd = async (externalSong) => {
    const deviceId = getDeviceId();
    const key = `${externalSong.source}:${externalSong.externalId}`;

    try {
      const res = await fetch(`${API_BASE}/api/temp-song-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: externalSong.title,
          artist: externalSong.artist,
          source: externalSong.source,
          externalId: externalSong.externalId,
          thumbnail: externalSong.thumbnail,
          device_id: deviceId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Request failed");

      setRequestedExternal((prev) => ({ ...prev, [key]: true }));

      if (data.message && data.status === "approved") {
        enqueueSnackbar(data.message, { variant: "info" });
      } else if (data.alreadyRequestedByDevice) {
        enqueueSnackbar("You already requested this song", { variant: "info" });
      } else {
        enqueueSnackbar(
          data.created
            ? "Thanks! Your song request was submitted."
            : `Request recorded (${data.request_count} total requests)`,
          { variant: "success" }
        );
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to submit request", { variant: "error" });
    }
  };

  const handleSubmitRequest = async (dedicationData) => {
    try {
      const deviceId = getDeviceId();
      const { userName, songs } = dedicationData;

      const finalUserName = userName || "Anonymous";
      setRequesterName(finalUserName);

      const canRequestResponse = await fetch(
        `${API_BASE}/api/song-requests/can-request?device_id=${deviceId}&song_id=${selectedSong.songs_master_id}`
      );

      const canRequestData = await canRequestResponse.json();

      if (canRequestData.hasActiveRequest) {
        enqueueSnackbar("You already have an active request for this song", {
          variant: "warning",
        });
        return;
      }

      const requestBody = {
        requester_name: finalUserName,
        device_id: deviceId,
        songs: songs.map(song => ({
          songs_master_id: song.songs_master_id,
          dedication_msg: song.dedication || "",
          action_type: song.actionType,
        })),
      };

      const response = await fetch(`${API_BASE}/api/song-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Failed to submit request");
      }

      await response.json();
      setShowDedicationModal(false);
      onClose();
      setShowSuccessModal(true);
      handleRequestSuccess();
    } catch (error) {
      console.error("Failed to make request:", error);
      enqueueSnackbar("Failed to submit request. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setShowThankYouDialog(true);
  };

  const handleThankYouClose = () => {
    setShowThankYouDialog(false);
    clearCart();
  };

  const isSearching = searchQuery.trim().length >= 2;
  const showHint = searchQuery.trim().length > 0 && searchQuery.trim().length < 2;
  const hasNoSearchResults =
    isSearching && !searchLoading && localResults.length === 0 && externalResults.length === 0;

  const renderLocalSongs = (songs) =>
    songs.map((song) => (
      <Box key={song.songs_master_id} sx={{ mb: 2 }}>
        <SongCard
          song={{ ...song, id: song.songs_master_id }}
          onRequestOpen={handleRequestOpen}
          activeRequests={activeRequests}
          cart={cart}
        />
      </Box>
    ));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: { xs: "90vw", sm: "80vw", md: "500px" },
            height: "100vh",
            overflowY: "auto",
            margin: { xs: "16px", sm: "24px" },
            borderRadius: "12px",
            border: "1px solid",
            backgroundColor: "#000000",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#000000",
            color: "#FFFFFF",
            padding: "16px 24px",
          }}
        >
          Search Songs
          <IconButton aria-label="close" onClick={onClose} sx={{ color: "#FFFFFF" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            backgroundColor: "#000000",
            color: "#FFFFFF",
            padding: "16px 24px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search song or artist..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              sx: {
                backgroundColor: "#1a1a1a",
                color: "#fff",
                borderRadius: "8px",
              },
            }}
            sx={{ mb: 2 }}
          />

          {showHint && (
            <Typography sx={{ textAlign: "center", py: 2, color: "rgba(255,255,255,0.5)" }}>
              Type at least 2 characters to search
            </Typography>
          )}

          {/* Empty query: show popular suggestions from DB */}
          {!isSearching && !showHint && (
            <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "#1DB954", fontWeight: 700, mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Popular in our library
              </Typography>
              {suggestionsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : suggestedSongs.length > 0 ? (
                renderLocalSongs(suggestedSongs)
              ) : (
                <Typography sx={{ textAlign: "center", py: 3, color: "rgba(255,255,255,0.5)" }}>
                  Search our catalog or request songs we do not have yet
                </Typography>
              )}
            </Box>
          )}

          {isSearching && searchLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {isSearching && !searchLoading && (
            <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
              {/* Section 1: In our library */}
              <Typography
                variant="subtitle2"
                sx={{ color: "#1DB954", fontWeight: 700, mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                In our library
              </Typography>
              {localResults.length > 0 ? (
                renderLocalSongs(localResults)
              ) : (
                <Typography sx={{ color: "rgba(255,255,255,0.45)", mb: 2, fontSize: "0.875rem" }}>
                  No matches in our catalog for this search
                </Typography>
              )}

              <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", my: 2 }} />

              {/* Section 2: Not in DB — external APIs */}
              <Typography
                variant="subtitle2"
                sx={{ color: "#FFA726", fontWeight: 700, mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Not in our library
              </Typography>
              {externalError && (
                <Typography sx={{ color: "rgba(255,152,0,0.8)", mb: 1, fontSize: "0.8rem" }}>
                  Online suggestions unavailable ({externalError}). You can still request songs admin adds manually.
                </Typography>
              )}
              {externalResults.length > 0 ? (
                externalResults.map((song) => {
                  const key = `${song.source}:${song.externalId}`;
                  return (
                    <ExternalSongCard
                      key={key}
                      song={song}
                      onRequestToAdd={handleRequestToAdd}
                      requested={!!requestedExternal[key]}
                    />
                  );
                })
              ) : !externalError ? (
                <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.875rem" }}>
                  No online matches found
                </Typography>
              ) : null}

              {hasNoSearchResults && (
                <Typography sx={{ textAlign: "center", py: 2, color: "rgba(255,255,255,0.5)" }}>
                  No songs found anywhere for this search
                </Typography>
              )}
            </Box>
          )}

          {requestsLoading && isSearching && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <DedicationMaster
        open={showDedicationModal}
        onClose={() => setShowDedicationModal(false)}
        song={selectedSong}
        onSubmit={handleSubmitRequest}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        requesterName={requesterName}
      />

      <ThankDialog
        open={showThankYouDialog}
        onClose={handleThankYouClose}
      />
    </>
  );
};

export default SearchModal;
